import {
    makeAutoObservable,
    runInAction
} from "mobx";

import {
    fetchCalendar,

    fetchCalendarSettings,
    updateCalendarSettings,

    createCalendarPeriod,
    updateCalendarPeriod,
    deleteCalendarPeriod,

    fetchCalendarPrices,
    saveCalendarDateRules,
    deleteCalendarPrices,

    fetchCalendarConnections,
    createCalendarConnection,
    updateCalendarConnection,
    deleteCalendarConnection,
    syncCalendarConnection,
    saveCalendarRange,
} from "../http/calendarApi";


const getErrorMessage = (
    error,
    fallbackMessage
) => {
    return (
        error.response?.data?.message ||
        error.message ||
        fallbackMessage
    );
};


const normalizeConnections = (data) => {
    if (Array.isArray(data)) {
        return data;
    }

    if (Array.isArray(data?.connections)) {
        return data.connections;
    }

    return [];
};


const DATE_RULE_FIELDS = new Set([
    "price",
    "minimumStayNights",
    "extraGuestFee",
    "checkInTime",
    "checkOutTime"
]);


const toDateKey = (value) => {
    if (!value) {
        return "";
    }

    if (typeof value === "string") {
        return value.slice(0, 10);
    }

    if (value instanceof Date) {
        const year = value.getFullYear();

        const month = String(
            value.getMonth() + 1
        ).padStart(2, "0");

        const day = String(
            value.getDate()
        ).padStart(2, "0");

        return `${year}-${month}-${day}`;
    }

    return "";
};


export default class CalendarStore {
    constructor() {
        this._addId = null;

        this._periods = [];
        this._connections = [];

        this._basePrice = null;
        this._dateRules = [];
        this._dateRuleByDate = new Map();

        this._baseMinimumStayNights = 1;
        this._baseMaxGuests = null;
        this._baseIncludedGuests = null;
        this._baseExtraGuestFee = 0;

        this._baseCheckInTime = "14:00:00";
        this._baseCheckOutTime = "12:00:00";

        this._range = null;
        this._dateFrom = null;
        this._dateTo = null;

        this._isLoading = false;
        this._isCalendarLoading = false;
        this._isConnectionsLoading = false;
        this._isRulesLoading = false;
        this._isSettingsLoading = false;
        this._isSettingsSaving = false;
        this._isSaving = false;

        this._syncingConnectionId = null;

        this._error = "";

        makeAutoObservable(
            this,
            {},
            {
                autoBind: true
            }
        );
    }


    // =========================
    // SETTERS
    // =========================

    setAddId(addId) {
        this._addId = addId
            ? Number(addId)
            : null;
    }


    setPeriods(periods) {
        this._periods = Array.isArray(periods)
            ? periods
            : [];
    }


    setConnections(connections) {
        this._connections =
            normalizeConnections(connections);
    }


    setDateRules(data) {
        if (
            data?.basePrice !==
            undefined
        ) {
            this._basePrice =
                data.basePrice == null
                    ? null
                    : Number(
                        data.basePrice
                    );
        }

        if (
            data?.baseMinimumStayNights !==
            undefined
        ) {
            this._baseMinimumStayNights =
                data.baseMinimumStayNights == null
                    ? 1
                    : Number(
                        data.baseMinimumStayNights
                    );
        }

        if (
            data?.baseExtraGuestFee !==
            undefined
        ) {
            this._baseExtraGuestFee =
                data.baseExtraGuestFee == null
                    ? 0
                    : Number(
                        data.baseExtraGuestFee
                    );
        }

        this._dateRules =
            Array.isArray(data?.prices)
                ? data.prices
                : Array.isArray(data?.dateRules)
                    ? data.dateRules
                    : [];

        /*
         * В CalendarDateRule хранятся только реальные
         * индивидуальные переопределения. Базовые значения
         * не копируем в правило даты.
         *
         * Индекс нужен и для корректной модели наследования,
         * и для быстрого чтения годовой шахматки.
         */
        this._dateRuleByDate =
            new Map(
                this._dateRules
                    .filter((rule) => rule?.date)
                    .map((rule) => [
                        String(rule.date).slice(0, 10),
                        rule
                    ])
            );
    }


    setSettings(data) {
        this._basePrice =
            data?.basePrice == null
                ? null
                : Number(
                    data.basePrice
                );

        this._baseMinimumStayNights =
            data?.minimumStayNights == null
                ? 1
                : Number(
                    data.minimumStayNights
                );

        this._baseMaxGuests =
            data?.maxGuests == null
                ? null
                : Number(
                    data.maxGuests
                );

        this._baseIncludedGuests =
            data?.includedGuests == null
                ? null
                : Number(
                    data.includedGuests
                );

        this._baseExtraGuestFee =
            data?.extraGuestFee == null
                ? 0
                : Number(
                    data.extraGuestFee
                );

        this._baseCheckInTime =
            data?.checkInTime ||
            "14:00:00";

        this._baseCheckOutTime =
            data?.checkOutTime ||
            "12:00:00";


        /*
         * Правила дат не переписываем.
         * Их null-поля означают наследование, поэтому
         * effective-значения всегда вычисляются на чтении
         * от актуальных стандартных условий.
         */
    }


    setRange(range) {
        this._range =
            range || null;
    }


    setError(error) {
        this._error =
            error || "";
    }


    clearError() {
        this._error = "";
    }


    // =========================
    // INITIAL LOAD
    // =========================

    async load(addId, params = {}) {
        const normalizedAddId =
            Number(addId);

        if (
            !Number.isInteger(
                normalizedAddId
            ) ||
            normalizedAddId <= 0
        ) {
            this.setError(
                "Некорректный id объявления"
            );

            return false;
        }

        this._addId =
            normalizedAddId;

        this._dateFrom =
            params.dateFrom || null;

        this._dateTo =
            params.dateTo || null;

        this._isLoading = true;
        this._isCalendarLoading = true;
        this._isConnectionsLoading = true;
        this._isRulesLoading = true;
        this._isSettingsLoading = true;

        this.clearError();

        try {
            const hasDateRange =
                Boolean(
                    this._dateFrom &&
                    this._dateTo
                );

            const [
                calendarData,
                connectionsData,
                pricesData,
                settingsData
            ] = await Promise.all([
                fetchCalendar(
                    normalizedAddId,
                    {
                        dateFrom:
                        this._dateFrom,

                        dateTo:
                        this._dateTo
                    }
                ),

                fetchCalendarConnections(
                    normalizedAddId
                ),

                hasDateRange
                    ? fetchCalendarPrices(
                        normalizedAddId,
                        {
                            dateFrom:
                            this._dateFrom,

                            dateTo:
                            this._dateTo
                        }
                    )
                    : Promise.resolve({
                        prices: []
                    }),

                fetchCalendarSettings(
                    normalizedAddId
                )
            ]);

            runInAction(() => {
                this._periods =
                    Array.isArray(
                        calendarData?.periods
                    )
                        ? calendarData.periods
                        : [];

                this._range =
                    calendarData?.range ||
                    null;

                this._connections =
                    normalizeConnections(
                        connectionsData
                    );

                /*
                 * Записываем индивидуальные правила:
                 * цену, минимум ночей, доплату и время.
                 */
                this.setDateRules(
                    pricesData
                );

                /*
                 * Записываем базовые настройки объявления
                 * и пересчитываем effective-значения правил.
                 *
                 * Важно вызывать после setDateRules().
                 */
                this.setSettings(
                    settingsData
                );
            });

            return true;
        } catch (error) {
            runInAction(() => {
                this._error =
                    getErrorMessage(
                        error,
                        "Не удалось загрузить календарь"
                    );
            });

            return false;
        } finally {
            runInAction(() => {
                this._isLoading = false;
                this._isCalendarLoading = false;
                this._isConnectionsLoading = false;
                this._isRulesLoading = false;
                this._isSettingsLoading = false;
            });
        }
    }


    async reloadCalendar() {
        if (!this._addId) {
            return false;
        }

        this._isCalendarLoading = true;

        this.clearError();

        try {
            const data =
                await fetchCalendar(
                    this._addId,
                    {
                        dateFrom:
                        this._dateFrom,

                        dateTo:
                        this._dateTo
                    }
                );

            runInAction(() => {
                this._periods =
                    Array.isArray(
                        data?.periods
                    )
                        ? data.periods
                        : [];

                this._range =
                    data?.range ||
                    null;
            });

            return true;
        } catch (error) {
            runInAction(() => {
                this._error =
                    getErrorMessage(
                        error,
                        "Не удалось обновить календарь"
                    );
            });

            return false;
        } finally {
            runInAction(() => {
                this._isCalendarLoading =
                    false;
            });
        }
    }


    async reloadConnections() {
        if (!this._addId) {
            return false;
        }

        this._isConnectionsLoading = true;

        this.clearError();

        try {
            const data =
                await fetchCalendarConnections(
                    this._addId
                );

            runInAction(() => {
                this._connections =
                    normalizeConnections(
                        data
                    );
            });

            return true;
        } catch (error) {
            runInAction(() => {
                this._error =
                    getErrorMessage(
                        error,
                        "Не удалось обновить подключения"
                    );
            });

            return false;
        } finally {
            runInAction(() => {
                this._isConnectionsLoading =
                    false;
            });
        }
    }


    async reloadDateRules() {
        if (
            !this._addId ||
            !this._dateFrom ||
            !this._dateTo
        ) {
            return false;
        }

        this._isRulesLoading = true;

        this.clearError();

        try {
            const data =
                await fetchCalendarPrices(
                    this._addId,
                    {
                        dateFrom:
                        this._dateFrom,

                        dateTo:
                        this._dateTo
                    }
                );

            runInAction(() => {
                this.setDateRules(
                    data
                );
            });

            return true;
        } catch (error) {
            runInAction(() => {
                this._error =
                    getErrorMessage(
                        error,
                        "Не удалось загрузить цены и правила"
                    );
            });

            return false;
        } finally {
            runInAction(() => {
                this._isRulesLoading =
                    false;
            });
        }
    }


    async reloadSettings() {
        if (!this._addId) {
            return false;
        }

        this._isSettingsLoading = true;

        this.clearError();

        try {
            const data =
                await fetchCalendarSettings(
                    this._addId
                );

            runInAction(() => {
                this.setSettings(data);
            });

            return true;
        } catch (error) {
            runInAction(() => {
                this._error =
                    getErrorMessage(
                        error,
                        "Не удалось загрузить базовые настройки календаря"
                    );
            });

            return false;
        } finally {
            runInAction(() => {
                this._isSettingsLoading =
                    false;
            });
        }
    }

    // =========================
    // COMPLETE RANGE UPDATE
    // =========================

    async saveRange(payload) {
        if (!this._addId) {
            return false;
        }

        this._isSaving = true;

        this.clearError();

        try {
            await saveCalendarRange(
                this._addId,
                payload
            );

            /*
             * /range возвращает только изменённый диапазон.
             * Поэтому перечитываем весь видимый календарь,
             * чтобы не потерять остальные периоды месяца.
             */
            const [
                calendarReloaded,
                rulesReloaded
            ] = await Promise.all([
                this.reloadCalendar(),
                this.reloadDateRules()
            ]);

            return (
                calendarReloaded &&
                rulesReloaded
            );
        } catch (error) {
            runInAction(() => {
                this._error =
                    getErrorMessage(
                        error,
                        "Не удалось сохранить настройки выбранных дат"
                    );
            });

            return false;
        } finally {
            runInAction(() => {
                this._isSaving = false;
            });
        }
    }


    // =========================
    // PERIODS
    // =========================

    async addPeriod(payload) {
        if (!this._addId) {
            return false;
        }

        this._isSaving = true;

        this.clearError();

        try {
            await createCalendarPeriod(
                this._addId,
                payload
            );

            await this.reloadCalendar();

            return true;
        } catch (error) {
            runInAction(() => {
                this._error =
                    getErrorMessage(
                        error,
                        "Не удалось добавить занятый период"
                    );
            });

            return false;
        } finally {
            runInAction(() => {
                this._isSaving = false;
            });
        }
    }


    async editPeriod(
        periodId,
        payload
    ) {
        if (
            !this._addId ||
            !periodId
        ) {
            return false;
        }

        this._isSaving = true;

        this.clearError();

        try {
            await updateCalendarPeriod(
                this._addId,
                periodId,
                payload
            );

            await this.reloadCalendar();

            return true;
        } catch (error) {
            runInAction(() => {
                this._error =
                    getErrorMessage(
                        error,
                        "Не удалось изменить занятый период"
                    );
            });

            return false;
        } finally {
            runInAction(() => {
                this._isSaving = false;
            });
        }
    }


    async removePeriod(periodId) {
        if (
            !this._addId ||
            !periodId
        ) {
            return false;
        }

        this._isSaving = true;

        this.clearError();

        try {
            await deleteCalendarPeriod(
                this._addId,
                periodId
            );

            await this.reloadCalendar();

            return true;
        } catch (error) {
            runInAction(() => {
                this._error =
                    getErrorMessage(
                        error,
                        "Не удалось удалить занятый период"
                    );
            });

            return false;
        } finally {
            runInAction(() => {
                this._isSaving = false;
            });
        }
    }


    // =========================
    // CALENDAR SETTINGS
    // =========================

    async saveSettings(payload) {
        if (!this._addId) {
            return false;
        }

        this._isSettingsSaving = true;

        this.clearError();

        try {
            const data =
                await updateCalendarSettings(
                    this._addId,
                    payload
                );

            runInAction(() => {
                this.setSettings(data);
            });

            return true;
        } catch (error) {
            runInAction(() => {
                this._error =
                    getErrorMessage(
                        error,
                        "Не удалось сохранить базовые настройки"
                    );
            });

            return false;
        } finally {
            runInAction(() => {
                this._isSettingsSaving =
                    false;
            });
        }
    }


    // =========================
    // PRICES AND DATE RULES
    // =========================

    async saveDateRules(payload) {
        if (!this._addId) {
            return false;
        }

        this._isSaving = true;

        this.clearError();

        try {
            await saveCalendarDateRules(
                this._addId,
                payload
            );

            await this.reloadDateRules();

            return true;
        } catch (error) {
            runInAction(() => {
                this._error =
                    getErrorMessage(
                        error,
                        "Не удалось сохранить цену и условия"
                    );
            });

            return false;
        } finally {
            runInAction(() => {
                this._isSaving = false;
            });
        }
    }


    /*
     * Возвращает только цену к базовой.
     * Время остаётся прежним.
     */
    async resetPrice({
                         dateStart,
                         dateEnd
                     }) {
        if (!this._addId) {
            return false;
        }

        this._isSaving = true;

        this.clearError();

        try {
            await deleteCalendarPrices(
                this._addId,
                {
                    dateStart,
                    dateEnd
                }
            );

            await this.reloadDateRules();

            return true;
        } catch (error) {
            runInAction(() => {
                this._error =
                    getErrorMessage(
                        error,
                        "Не удалось вернуть базовую цену"
                    );
            });

            return false;
        } finally {
            runInAction(() => {
                this._isSaving = false;
            });
        }
    }


    /*
     * Полностью очищает индивидуальные правила:
     * цену, минимум ночей, доплату за гостя
     * и время заезда/выезда.
     */
    async clearDateRules({
                             dateStart,
                             dateEnd
                         }) {
        return this.saveDateRules({
            dateStart,
            dateEnd,

            price: null,
            minimumStayNights: null,
            extraGuestFee: null,
            checkInTime: null,
            checkOutTime: null
        });
    }


    // =========================
    // CONNECTIONS
    // =========================

    async addConnection(payload) {
        if (!this._addId) {
            return false;
        }

        this._isSaving = true;

        this.clearError();

        try {
            await createCalendarConnection(
                this._addId,
                payload
            );

            await this.reloadConnections();

            return true;
        } catch (error) {
            runInAction(() => {
                this._error =
                    getErrorMessage(
                        error,
                        "Не удалось добавить подключение"
                    );
            });

            return false;
        } finally {
            runInAction(() => {
                this._isSaving = false;
            });
        }
    }


    async editConnection(
        connectionId,
        payload
    ) {
        if (
            !this._addId ||
            !connectionId
        ) {
            return false;
        }

        this._isSaving = true;

        this.clearError();

        try {
            await updateCalendarConnection(
                this._addId,
                connectionId,
                payload
            );

            await this.reloadConnections();

            return true;
        } catch (error) {
            runInAction(() => {
                this._error =
                    getErrorMessage(
                        error,
                        "Не удалось изменить подключение"
                    );
            });

            return false;
        } finally {
            runInAction(() => {
                this._isSaving = false;
            });
        }
    }


    async removeConnection(connectionId) {
        if (
            !this._addId ||
            !connectionId
        ) {
            return false;
        }

        this._isSaving = true;

        this.clearError();

        try {
            await deleteCalendarConnection(
                this._addId,
                connectionId
            );

            await Promise.all([
                this.reloadConnections(),
                this.reloadCalendar()
            ]);

            return true;
        } catch (error) {
            runInAction(() => {
                this._error =
                    getErrorMessage(
                        error,
                        "Не удалось удалить подключение"
                    );
            });

            return false;
        } finally {
            runInAction(() => {
                this._isSaving = false;
            });
        }
    }


    async syncConnection(
        connectionId
    ) {
        if (
            !this._addId ||
            !connectionId
        ) {
            return false;
        }


        /*
         * Пока синхронизируется одно подключение,
         * вторую синхронизацию не запускаем.
         */
        if (
            this._syncingConnectionId !==
            null
        ) {
            return false;
        }


        const normalizedConnectionId =
            Number(connectionId);


        this._syncingConnectionId =
            normalizedConnectionId;

        this.clearError();


        try {
            await syncCalendarConnection(
                this._addId,
                normalizedConnectionId
            );


            /*
             * После успешной синхронизации:
             *
             * - перечитываем подключение, чтобы получить
             *   lastSyncAt, lastSuccessAt и очищенный lastError;
             *
             * - перечитываем периоды, чтобы изменения
             *   сразу появились на календаре.
             */
            const [
                connectionsReloaded,
                calendarReloaded
            ] = await Promise.all([
                this.reloadConnections(),
                this.reloadCalendar()
            ]);


            if (
                !connectionsReloaded ||
                !calendarReloaded
            ) {
                if (!this._error) {
                    runInAction(() => {
                        this._error =
                            "Синхронизация выполнена, но не удалось обновить данные страницы";
                    });
                }

                return false;
            }


            return true;
        } catch (error) {
            const errorMessage =
                getErrorMessage(
                    error,
                    "Не удалось синхронизировать календарь"
                );


            /*
             * Сервер при неудачной попытке записывает
             * lastSyncAt и lastError.
             *
             * Поэтому после ошибки отдельно перечитываем
             * подключения, но сохраняем исходный текст ошибки.
             */
            try {
                const data =
                    await fetchCalendarConnections(
                        this._addId
                    );


                runInAction(() => {
                    this._connections =
                        normalizeConnections(
                            data
                        );

                    this._error =
                        errorMessage;
                });
            } catch {
                runInAction(() => {
                    this._error =
                        errorMessage;
                });
            }


            return false;
        } finally {
            runInAction(() => {
                this._syncingConnectionId =
                    null;
            });
        }
    }


    // =========================
    // DATE HELPERS
    // =========================

    getPeriodsForDate(date) {
        const dateKey =
            toDateKey(date);

        if (!dateKey) {
            return [];
        }

        return this._periods.filter(
            (period) => {
                return (
                    period.dateStart <=
                    dateKey &&
                    dateKey <
                    period.dateEnd
                );
            }
        );
    }


    isDateBusy(date) {
        return (
            this.getPeriodsForDate(
                date
            ).length > 0
        );
    }


    getRuleForDate(date) {
        const dateKey =
            toDateKey(date);

        if (!dateKey) {
            return null;
        }

        return (
            this._dateRuleByDate.get(
                dateKey
            ) || null
        );
    }


    /*
     * Effective-значения принципиально НЕ берём из
     * сохранённого снимка rule.effective*.
     *
     * Источник истины:
     * - ненулевое поле CalendarDateRule = ручное значение;
     * - null = всегда актуальное стандартное значение квартиры.
     *
     * Благодаря этому изменение стандартных условий сразу
     * отражается на всех датах, где конкретное поле не было
     * переопределено владельцем.
     */
    getEffectivePriceForDate(date) {
        const rule =
            this.getRuleForDate(date);

        if (rule?.price != null) {
            return Number(
                rule.price
            );
        }

        return this._basePrice;
    }

    getEffectiveMinimumStayForDate(
        date
    ) {
        const rule =
            this.getRuleForDate(
                date
            );

        if (
            rule?.minimumStayNights !=
            null
        ) {
            return Number(
                rule.minimumStayNights
            );
        }

        return this._baseMinimumStayNights;
    }


    getEffectiveExtraGuestFeeForDate(
        date
    ) {
        const rule =
            this.getRuleForDate(
                date
            );

        if (
            rule?.extraGuestFee !=
            null
        ) {
            return Number(
                rule.extraGuestFee
            );
        }

        return this._baseExtraGuestFee;
    }

    hasCustomPriceForDate(date) {
        const rule =
            this.getRuleForDate(date);

        return (
            rule?.price != null
        );
    }


    hasCustomFieldForDate(
        date,
        fieldName
    ) {
        if (
            !DATE_RULE_FIELDS.has(
                fieldName
            )
        ) {
            return false;
        }

        const rule =
            this.getRuleForDate(date);

        return (
            rule?.[fieldName] != null
        );
    }


    getCheckInTimeForDate(date) {
        return (
            this.getRuleForDate(date)
                ?.checkInTime ||
            null
        );
    }


    getCheckOutTimeForDate(date) {
        return (
            this.getRuleForDate(date)
                ?.checkOutTime ||
            null
        );
    }


    getEffectiveCheckInTimeForDate(
        date
    ) {
        return (
            this.getCheckInTimeForDate(
                date
            ) ||
            this._baseCheckInTime
        );
    }


    getEffectiveCheckOutTimeForDate(
        date
    ) {
        return (
            this.getCheckOutTimeForDate(
                date
            ) ||
            this._baseCheckOutTime
        );
    }


    getConnectionById(
        connectionId
    ) {
        return (
            this._connections.find(
                (connection) => {
                    return (
                        Number(
                            connection.id
                        ) ===
                        Number(
                            connectionId
                        )
                    );
                }
            ) || null
        );
    }


    isConnectionSyncing(
        connectionId
    ) {
        return (
            Number(
                this._syncingConnectionId
            ) ===
            Number(connectionId)
        );
    }


    reset() {
        this._addId = null;

        this._periods = [];
        this._connections = [];

        this._basePrice = null;
        this._dateRules = [];
        this._dateRuleByDate = new Map();

        this._baseMinimumStayNights = 1;
        this._baseMaxGuests = null;
        this._baseIncludedGuests = null;
        this._baseExtraGuestFee = 0;

        this._baseCheckInTime =
            "14:00:00";

        this._baseCheckOutTime =
            "12:00:00";

        this._range = null;
        this._dateFrom = null;
        this._dateTo = null;

        this._isLoading = false;
        this._isCalendarLoading = false;
        this._isConnectionsLoading = false;
        this._isRulesLoading = false;
        this._isSettingsLoading = false;
        this._isSettingsSaving = false;
        this._isSaving = false;

        this._syncingConnectionId =
            null;

        this._error = "";
    }


    // =========================
    // GETTERS
    // =========================

    get addId() {
        return this._addId;
    }


    get periods() {
        return this._periods;
    }


    get connections() {
        return this._connections;
    }


    get basePrice() {
        return this._basePrice;
    }

    get baseMinimumStayNights() {
        return this._baseMinimumStayNights;
    }


    get baseMaxGuests() {
        return this._baseMaxGuests;
    }


    get baseIncludedGuests() {
        return this._baseIncludedGuests;
    }


    get baseExtraGuestFee() {
        return this._baseExtraGuestFee;
    }


    get baseCheckInTime() {
        return this._baseCheckInTime;
    }


    get baseCheckOutTime() {
        return this._baseCheckOutTime;
    }


    get dateRules() {
        return this._dateRules;
    }


    get range() {
        return this._range;
    }


    get isLoading() {
        return this._isLoading;
    }


    get isCalendarLoading() {
        return this._isCalendarLoading;
    }


    get isConnectionsLoading() {
        return this._isConnectionsLoading;
    }


    get isRulesLoading() {
        return this._isRulesLoading;
    }


    get isSettingsLoading() {
        return this._isSettingsLoading;
    }


    get isSettingsSaving() {
        return this._isSettingsSaving;
    }


    get isSaving() {
        return this._isSaving;
    }


    get syncingConnectionId() {
        return this._syncingConnectionId;
    }


    get error() {
        return this._error;
    }
}